import { ColDef, KeyCreatorParams, ValueFormatterParams } from 'ag-grid-community';
import { SetFilterValuesFuncParams } from 'ag-grid-enterprise';
import { LicenseNumberActionButtonsCellRendererComponent } from '@workspace-app/shared';
import { CoaterLicense } from '@workspace-src/app/odatabackend/QualicoatBackendModel';
import { LinesCellRendererComponent } from './lines-cell-renderer.component';
import { RenewalStatusCellRendererComponent } from './renewal-status-cell-renderer.component';

interface SetOption {
  label: string;
  value: string;
}

export function getLicensesColumns(
  caller?: { openTimeline: (license: CoaterLicense) => void },
  options?: { glNamesFetcher?: (params: SetFilterValuesFuncParams) => void }
): ColDef[] {
  const columns: ColDef[] = [];

  columns.push({
    headerName: 'Actions',
    cellRenderer: LicenseNumberActionButtonsCellRendererComponent,
    cellRendererParams: {
      context: {
        testId: 'license-actions-button',
        timelineTestId: 'timeline-license-button',
        deleteTestId: 'delete-inspection-button',
        timelineFn: caller ? (licenseNumber: CoaterLicense) => caller.openTimeline(licenseNumber) : undefined,
        // Removed grantFn and withdrawFn - these actions are now only in the license view
      },
    },
    cellClass: 'action-buttons-cell',
    pinned: 'left',
    lockPosition: 'left',
    suppressMovable: true,
    minWidth: 50,
    width: 50,
    maxWidth: 50,
  });

  columns.push(
    {
      field: 'completeLicenseNumber',
      headerName: 'License Number',
      sortable: true,
      filter: 'agTextColumnFilter',
      filterParams: {
        maxNumConditions: 1,
        filterOptions: ['equals', 'contains'],
        buttons: ['reset'],
      },
      floatingFilter: false,
    },
    {
      field: 'licenseType',
      headerName: 'License Type',
      sortable: true,
      filter: 'agSetColumnFilter',
      filterParams: {
        defaultToNothingSelected: true,
        values: (params: SetFilterValuesFuncParams<SetOption, SetOption>) => {
          return params.success([
            {
              label: 'Coater License',
              value: 'Coater License',
            },
            {
              label: 'Powder Supplier License',
              value: 'Powder Supplier License',
            },
            {
              label: 'Decorator License',
              value: 'Decorator License',
            },
            {
              label: 'Film Supplier License',
              value: 'Film Supplier License',
            },
          ]);
        },
        keyCreator: (params: KeyCreatorParams) => params.value.value,
        valueFormatter: (params: ValueFormatterParams) => params.value.label,
        excelMode: 'windows',
        buttons: ['reset'],
      },
      floatingFilter: false,
    },
    {
      field: 'productionSite.customer.name',
      headerName: 'Customer',
      sortable: true,
      filter: 'agTextColumnFilter',
      filterParams: {
        maxNumConditions: 1,
        filterOptions: ['contains'],
        buttons: ['reset'],
      },
      floatingFilter: false,
    },
    {
      field: 'issueDate',
      cellDataType: 'date',
      filter: 'agDateColumnFilter',
      filterParams: {
        maxNumConditions: 1,
        buttons: ['reset'],
        filterOptions: ['equals', 'lessThan', 'greaterThan', 'inRange'],
      },
      sortable: true,
      valueFormatter: (params: ValueFormatterParams) => {
        if (!params.value) return '';
        return new Date(params.value).toLocaleDateString('en-GB', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          timeZone: 'UTC',
        });
      },
    },
    {
      field: 'expiryDate',
      cellDataType: 'date',
      filter: 'agDateColumnFilter',
      filterParams: {
        maxNumConditions: 1,
        buttons: ['reset'],
        filterOptions: ['equals', 'lessThan', 'greaterThan', 'inRange'],
      },
      sortable: true,
      valueFormatter: (params: ValueFormatterParams) => {
        if (!params.value) return '';
        return new Date(params.value).toLocaleDateString('en-GB', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          timeZone: 'UTC',
        });
      },
    },
    {
      field: 'withdrawalDate',
      cellDataType: 'date',
      initialHide: true,
      filter: 'agDateColumnFilter',
      filterParams: {
        maxNumConditions: 1,
        buttons: ['reset'],
        filterOptions: ['equals', 'lessThan', 'greaterThan', 'inRange'],
      },
      sortable: true,
      valueFormatter: (params: ValueFormatterParams) => {
        if (!params.value) return '';
        return new Date(params.value).toLocaleDateString('en-GB', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          timeZone: 'UTC',
        });
      },
    },
    {
      field: 'withdrawalReason',
      headerName: 'Withdrawal Reason',
      initialHide: true,
      sortable: true,
      filter: 'agTextColumnFilter',
      filterParams: {
        maxNumConditions: 1,
        filterOptions: ['contains'],
        buttons: ['reset'],
      },
      floatingFilter: false,
    },
    {
      field: 'suspensionDate',
      cellDataType: 'date',
      initialHide: true,
      filter: 'agDateColumnFilter',
      filterParams: {
        maxNumConditions: 1,
        buttons: ['reset'],
        filterOptions: ['equals', 'lessThan', 'greaterThan', 'inRange'],
      },
      sortable: true,
      valueFormatter: (params: ValueFormatterParams) => {
        if (!params.value) return '';
        return new Date(params.value).toLocaleDateString('en-GB', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          timeZone: 'UTC',
        });
      },
    },
    {
      field: 'suspensionReason',
      headerName: 'Suspension Reason',
      initialHide: true,
      sortable: true,
      filter: 'agTextColumnFilter',
      filterParams: {
        maxNumConditions: 1,
        filterOptions: ['contains'],
        buttons: ['reset'],
      },
      floatingFilter: false,
    },
    {
      field: 'renewalDisplayStatus',
      headerName: 'License Renewal',
      sortable: false,
      filter: 'agSetColumnFilter',
      filterParams: {
        values: ['In Progress', 'Certificate Ready', 'Cancelled'],
        defaultToNothingSelected: true,
        buttons: ['reset'],
      },
      floatingFilter: false,
      cellRenderer: RenewalStatusCellRendererComponent,
      minWidth: 200,
      maxWidth: 250,
    },
    {
      field: 'lines',
      headerName: 'Lines',
      sortable: false,
      initialHide: false,
      minWidth: 120,
      maxWidth: 150,
      valueGetter: (params) => {
        // Get line extensions from the license - might be a JSON string or already parsed
        const lineExtensionsData = params.data?.lineExtensions;
        const validationRemarks = params.data?.lineValidationRemarks || [];
        if (lineExtensionsData) {
          let lineExtensions;
          // Check if it's already an object/array or needs parsing
          if (typeof lineExtensionsData === 'string') {
            try {
              lineExtensions = JSON.parse(lineExtensionsData);
            } catch (e) {
              console.error('Failed to parse lineExtensions:', e);
              return { count: 0, hasSeaside: false, hasAnodic: false, hasPostforming: false, validationRemarks: [] };
            }
          } else {
            lineExtensions = lineExtensionsData;
          }

          if (Array.isArray(lineExtensions)) {
            const count = lineExtensions.length;
            const hasSeaside = lineExtensions.some(
              (line: { Seaside?: boolean; seaside?: boolean }) => line.Seaside === true || line.seaside === true
            );
            const hasAnodic = lineExtensions.some(
              (line: { anodicPretreatment?: boolean; AnodicPretreatment?: boolean }) =>
                line.anodicPretreatment === true || line.AnodicPretreatment === true
            );
            const hasPostforming = lineExtensions.some(
              (line: { postforming?: boolean; Postforming?: boolean }) =>
                line.postforming === true || line.Postforming === true
            );
            return { count, hasSeaside, hasAnodic, hasPostforming, validationRemarks };
          }
        }
        return { count: 0, hasSeaside: false, hasAnodic: false, hasPostforming: false, validationRemarks: [] };
      },
      cellRenderer: LinesCellRendererComponent,
      filter: false,
      floatingFilter: false,
    },
    {
      field: 'licenseState',
      headerName: 'License State',
      sortable: false,
      filter: 'agSetColumnFilter',
      filterParams: {
        values: (params: SetFilterValuesFuncParams<SetOption, SetOption>) => {
          return params.success([
            {
              label: 'Granting',
              value: 'Granting',
            },
            {
              label: 'Valid',
              value: 'Valid',
            },
            {
              label: 'Withdrawn',
              value: 'Withdrawn',
            },
            {
              label: 'Suspended',
              value: 'Suspended',
            },
            {
              label: 'Expiring',
              value: 'Expiring',
            },
            {
              label: 'Expired',
              value: 'Expired',
            },
          ]);
        },
        keyCreator: (params: KeyCreatorParams) => params.value.value,
        valueFormatter: (params: ValueFormatterParams) => params.value.label,
        excelMode: 'windows',
        buttons: ['reset'],
      },
      floatingFilter: false,
    },
    {
      field: 'productionSite.customer.assignedGl.name',
      headerName: 'Assigned GL',
      sortable: true,
      initialHide: true,
      filter: 'agSetColumnFilter',
      filterParams: {
        defaultToNothingSelected: true,
        values: options?.glNamesFetcher
          ? (params: SetFilterValuesFuncParams) => options.glNamesFetcher!(params)
          : [],
        buttons: ['reset'],
      },
      floatingFilter: false,
      minWidth: 150,
      width: 200,
      maxWidth: 250,
    }
  );

  return columns;
}
